import { css } from '@emotion/css';
import {
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { isEmail, useForm } from '@mantine/form';
import {
  IconArrowRight,
  IconCalendarClock,
  IconFileAlert,
  IconFileDollar,
  IconInvoice,
  IconListCheck,
  IconReport,
  IconUserDollar,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { upperCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { isEmpty } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import { useCreateReport } from 'shared/hooks/use-create-report';
import { type Reports, useLoadReports } from 'shared/hooks/use-load-reports';
import { useLoadViews } from 'shared/hooks/use-load-views';

import { NameStep, PeriodicityStep, RecipientsStep } from './steps';

const styles = {
  resourceButton: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-radius: var(--mantine-radius-md);
    height: rem(90px);
    background-color: light-dark(var(--mantine-color-white), var(--mantine-color-dark-7));
    transition:
      box-shadow 200ms ease,
      transform 200ms ease,
      border-color 200ms ease;

    :hover {
      box-shadow: var(--mantine-shadow-sm);
      transform: scale(1.03);
    }
  `,
  resourceButtonText: css`
    transition: color 200ms ease;
  `,
};

const iconStyles = { size: 32, stroke: 1.5, color: 'var(--mantine-color-purple-6)' };

export const RESOURCES = [
  { slug: 'debtors', name: 'DEBTORS', icon: <IconUserDollar {...iconStyles} /> },
  { slug: 'invoices', name: 'INVOICES', icon: <IconFileDollar {...iconStyles} /> },
  {
    slug: 'postponable_invoices',
    name: 'REMINDERS',
    icon: <IconCalendarClock {...iconStyles} />,
  },
  { slug: 'credit_notes', name: 'CREDIT_NOTES', icon: <IconInvoice {...iconStyles} /> },
  { slug: 'tasks', name: 'TASKS', icon: <IconListCheck {...iconStyles} /> },
  {
    slug: 'actionable_invoices',
    name: 'ACTIONABLE_INVOICES',
    icon: <IconFileAlert {...iconStyles} />,
  },
] as const;

export const CreateReport = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const client = useQueryClient();
  const { createReport, isCreateReportLoading } = useCreateReport();

  const [step, setStep] = useState(0);
  const [highestStepVisited, setHighestStepVisited] = useState(0);
  const [subtitles, setSubtitles] = useState({ 0: '', 1: '', 2: '' });

  const setSubtitle = (index: number, subtitle: string) => {
    setSubtitles(state => ({ ...state, [index]: subtitle }));
  };

  // it's only here to guarantee that the data will be in cache for the form validation function below
  // eslint-disable-next-line
  const {} = useLoadReports();

  useEffect(() => {
    if (step > highestStepVisited) setHighestStepVisited(step);
  }, [step]);

  // TODO: extract the form and expose the validation
  // TOOD: Extract the form type and use it in the props of lower components to remove the two `@ts-expect-error` below
  const form = useForm({
    validateInputOnChange: true,
    initialValues: {
      name: '',
      viewId: null as string | null,
      resourceType: 'debtors' as (typeof RESOURCES)[number]['slug'],
      periodicity: 'weekly' as 'weekly' | 'monthly',
      daysWeek: [] as Array<number>,
      daysMonth: [] as Array<Date>,
      // TODO: rename collaboratorEmails, externalEmails
      collaborators: [] as Array<string>,
      emails: [] as Array<string>,
    },
    validate: {
      name: value => {
        const exportTasks: Reports = client.getQueryData(['reports']) ?? [];
        const exportTasksNames = exportTasks.map(task => task.name);

        if (exportTasksNames.includes(value))
          return t(i18nKeys.REPORTS.NEW.STEPS.NAME.DUPLICATED_NAME);
        return null;
      },
      emails: value => {
        let error: null | string = null;

        value.forEach(email => {
          if (isEmail(true)(email))
            error = `${email} ${t(i18nKeys.REPORTS.NEW.STEPS.RECIPIENTS.INVALID_EMAIL)}`;
        });

        return error;
      },
    },
  });

  const { views, isViewsFetching } = useLoadViews({ resourceType: form.values.resourceType });

  const viewsForSelect = (views ?? []).map(view => ({ value: view.id, label: view.name }));

  useEffect(() => {
    form.setFieldValue('viewId', viewsForSelect[0]?.value ?? null);
  }, [views]);

  // TODO: the content of form.values is untyped!
  // TODO: Write a DtO to centralize this code which is replicated in the edit modals
  const handleCreateReport = () => {
    createReport(
      {
        viewId: form.values.viewId!,
        name: form.values.name,
        emails: [...form.values.emails, ...form.values.collaborators],
        periodicity: {
          type: form.values.periodicity,
          days:
            form.values.periodicity === 'weekly'
              ? form.values.daysWeek
              : // todo: should all be dayjs
                form.values.daysMonth.map(date => new Date(date).getDate()),
        },
      },
      {
        onSuccess: () => {
          history.push('/reports');
        },
      },
    );
  };

  const wasStepVisited = (index: number) => index <= highestStepVisited;

  return (
    <Stack>
      <PageTitle>
        {t(i18nKeys.REPORTS.NEW.TITLE)}
        <PageTitle.Actions>
          <Button
            onClick={() => {
              history.push('/reports');
            }}
            color="orange"
            variant="light"
          >
            {t(i18nKeys.REPORTS.NEW.CANCEL)}
          </Button>
        </PageTitle.Actions>
      </PageTitle>
      <Panel>
        <Stepper
          active={step}
          onStepClick={newStep => {
            setStep(newStep);
            setSubtitle(0, form.values.name);
            if (wasStepVisited(1)) {
              setSubtitle(
                1,
                viewsForSelect.find(view => view.value === form.values.viewId)?.label!,
              );
            }
            if (wasStepVisited(2))
              setSubtitle(
                2,
                t((i18nKeys.DATES.PERIOD_NAMES as any)[upperCase(form.values.periodicity)]),
              );
          }}
        >
          <Stepper.Step
            label={t(i18nKeys.REPORTS.NEW.STEP_LABELS.NAME)}
            description={subtitles[0]}
            allowStepSelect
          >
            <Center my="xl">
              <Stack miw="33%">
                <NameStep formProps={form.getInputProps('name')} />
                <Button
                  disabled={!form.values.name || form.errors.name != null}
                  w="100%"
                  rightSection={<IconArrowRight />}
                  onClick={() => {
                    setSubtitle(0, form.values.name);
                    setStep(1);
                  }}
                >
                  {t(i18nKeys.REPORTS.NEW.STEPS.ADVANCE)}
                </Button>
              </Stack>
            </Center>
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.REPORTS.NEW.STEP_LABELS.DATA)}
            allowStepSelect={
              (highestStepVisited >= 1 && form.values.name !== '') || form.errors.name != null
            }
            description={subtitles[1]}
          >
            <Group my="xl" justify="center" align="start">
              <Stack w="33%">
                <Text size="lg" fw={450}>
                  {t(i18nKeys.REPORTS.NEW.STEPS.DATA.RESOURCE_TYPE)}
                </Text>
                <Paper p="xl" bg="gray.1" shadow="none">
                  <SimpleGrid cols={2}>
                    {RESOURCES.map(resource => {
                      const isSelected = form.values.resourceType === resource.slug;
                      return (
                        <UnstyledButton
                          onClick={() => form.setFieldValue('resourceType', resource.slug)}
                          style={{
                            border: isSelected
                              ? '2px solid var(--mantine-color-purple-4)'
                              : '2px solid transparent',
                          }}
                          key={resource.slug}
                          p="sm"
                          className={styles.resourceButton}
                        >
                          {resource.icon}
                          <Text
                            mt="xs"
                            c={isSelected ? 'purple.6' : 'gray.8'}
                            fw={isSelected ? 500 : 400}
                            className={styles.resourceButtonText}
                          >
                            {t(i18nKeys.REPORTS.NEW.STEPS.DATA.RESOURCE_TYPES[resource.name])}
                          </Text>
                        </UnstyledButton>
                      );
                    })}
                  </SimpleGrid>
                </Paper>
              </Stack>
              <Divider orientation="vertical" mx="lg" />
              <Stack w="33%">
                <Text size="lg" fw={450}>
                  {t(i18nKeys.REPORTS.NEW.STEPS.DATA.VIEW)}
                </Text>
                <Group pos="relative">
                  <Select
                    {...form.getInputProps('viewId')}
                    w="100%"
                    disabled={isEmpty(viewsForSelect)}
                    checkIconPosition="right"
                    data={viewsForSelect}
                    searchable
                    allowDeselect={false}
                    placeholder="Select a view to export"
                  />
                  {isViewsFetching ? (
                    <Center
                      style={{ borderRadius: 'var(--mantine-radius-sm)' }}
                      pos="absolute"
                      w="100%"
                      h="100%"
                      bg="rgba(225, 225, 225, 0.9)"
                    >
                      <Loader size={25} />
                    </Center>
                  ) : null}
                </Group>
                <Text c="dimmed">
                  <HTML html={t(i18nKeys.REPORTS.NEW.STEPS.DATA.VIEW_SUBTITLE)} />
                </Text>
                <Button
                  w="100%"
                  rightSection={<IconArrowRight />}
                  onClick={() => {
                    setSubtitle(
                      1,
                      viewsForSelect.find(view => view.value === form.values.viewId)?.label!,
                    );
                    setStep(2);
                  }}
                >
                  {t(i18nKeys.REPORTS.NEW.STEPS.ADVANCE)}
                </Button>
              </Stack>
            </Group>
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.REPORTS.NEW.STEP_LABELS.RECURRENCE)}
            allowStepSelect={highestStepVisited >= 2}
            description={subtitles[2]}
          >
            <Center my="xl">
              <Stack>
                {/* @ts-ignore */}
                <PeriodicityStep form={form} />
                <Button
                  disabled={isEmpty(form.values.daysMonth) && isEmpty(form.values.daysWeek)}
                  w="100%"
                  rightSection={<IconArrowRight />}
                  onClick={() => {
                    setSubtitle(
                      2,
                      t(tKeysLoose.DATES.PERIOD_NAMES[upperCase(form.values.periodicity)]),
                    );
                    setStep(3);
                  }}
                >
                  {t(i18nKeys.REPORTS.NEW.STEPS.ADVANCE)}
                </Button>
              </Stack>
            </Center>
          </Stepper.Step>
          <Stepper.Step
            label={t(i18nKeys.REPORTS.NEW.STEP_LABELS.RECIPIENTS)}
            allowStepSelect={
              highestStepVisited >= 3 &&
              !(isEmpty(form.values.daysMonth) && isEmpty(form.values.daysWeek))
            }
          >
            {/* todo: wrap all steps the same */}
            <Center my="xl">
              <Stack w="33%" gap="lg">
                {/* @ts-ignore */}
                <RecipientsStep form={form} />
                <Button
                  loading={isCreateReportLoading}
                  disabled={
                    (isEmpty(form.values.collaborators) && isEmpty(form.values.emails)) ||
                    form.errors.emails != null
                  }
                  w="100%"
                  mt="xl"
                  rightSection={<IconReport stroke={1.5} />}
                  onClick={handleCreateReport}
                >
                  {t(i18nKeys.REPORTS.NEW.STEPS.CREATE)}
                </Button>
              </Stack>
            </Center>
          </Stepper.Step>
        </Stepper>
      </Panel>
    </Stack>
  );
};
